import axios from "axios";
import { useEffect, useState } from "react";
import { StorageGetItem, StorageSetItem, STORAGE_KEYS } from "./StorageComp";

// Add-on keys, shared with the backend (addons.key)
export const ADDONS = {
  ROUTE_OPTIMIZATION: "route_optimization",
  LIVE_TRACKING: "live_tracking",
  MEMBERSHIP: "membership",
  REFERRAL: "referral",
  DRIVER_APP: "driver_app",
  CUSTOMER_APP: "customer_app",
  AIR_FREIGHT: "air_freight",
  SHIPPING_DELIVERY: "shipping_delivery",
  SHOP: "shop",
};

export const ADDON_NAMES = {
  route_optimization: "Driver Route Optimization",
  live_tracking: "Track Drivers",
  membership: "Membership Discount",
  referral: "Referral System",
  driver_app: "Driver App",
  customer_app: "Customer App",
  air_freight: "Air Freight",
  shipping_delivery: "Shipping & Delivery",
  shop: "Shop",
};

// The auth cookie must stay small, so only the fields the UI reads are kept
export const compactBilling = (summary) => {
  if (!summary) return null;
  return {
    status: summary.status,
    suspended_reason: summary.suspended_reason,
    trial_ends_at: summary.trial_ends_at,
    next_billing_date: summary.next_billing_date,
    next_invoice_date: summary.next_invoice_date,
    monthly_estimate: summary.monthly_estimate,
    cancels_at: summary.cancels_at || null,
    features: summary.features || [],
    outstanding: (summary.outstanding_invoices || []).map((i) => ({ id: i.id, number: i.number, total: i.total, due_date: i.due_date, suspend_on: i.suspend_on, status: i.status })),
  };
};

const currentAuth = () => StorageGetItem(STORAGE_KEYS.AUTH);

export const isSuperAdmin = (auth = currentAuth()) => auth?.user_type === 1;

// Admin users read features from the billing summary; customers get a plain list at login
export const getFeatures = (auth = currentAuth()) => auth?.subscription?.features || auth?.features || [];

export const hasFeature = (key, auth = currentAuth()) => {
  if (!key) return true;
  if (isSuperAdmin(auth)) return true;
  return getFeatures(auth).indexOf(key) > -1;
};

// No service: suspended for an unpaid invoice, or the subscription has ended (unsubscribed)
export const isSuspended = (auth = currentAuth()) => !isSuperAdmin(auth) && (["suspended", "cancelled"].indexOf(auth?.subscription?.status) > -1 || auth?.service_active === false);

// Write a fresh billing summary into the auth cookie
export const saveBilling = (summary) => {
  const auth = currentAuth();
  if (!auth || !summary) return auth;
  const updated = { ...auth, subscription: compactBilling(summary), is_expired: summary.status === "suspended" || summary.status === "cancelled" };
  StorageSetItem(STORAGE_KEYS.AUTH, updated, 24 * 60 * 60 * 1000);
  window.dispatchEvent(new Event("efl-billing-updated"));
  return updated;
};

// Re-read billing for company users (staff pick up add-on changes made by the admin)
export const refreshBilling = () => {
  const auth = currentAuth();
  if (!auth?.api_token || isSuperAdmin(auth) || auth?.user_type === 5) return Promise.resolve(auth);
  return axios.get(process.env.REACT_APP_API_URL + "v1/billing/summary", {
    headers: { Authorization: `Bearer ${auth.api_token}` },
  }).then((response) => {
    if (response.data.status === "SUCCESS") {
      return saveBilling(response.data.data);
    }
    return auth;
  }).catch(() => auth);
};

// PDFs/CSVs need the bearer token, so they are fetched as a blob and saved
export const downloadFile = (path, filename, params) => {
  const auth = currentAuth();
  return axios.get(process.env.REACT_APP_API_URL + path, {
    params,
    responseType: "blob",
    headers: { Authorization: `Bearer ${auth?.api_token}` },
  }).then((response) => {
    const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  });
};

// Re-render when billing changes (add-on toggled, summary refreshed)
export const useBillingUpdates = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    window.addEventListener("efl-billing-updated", bump);
    return () => window.removeEventListener("efl-billing-updated", bump);
  }, []);
};

export const formatGBP = (value) => {
  const num = Number(value || 0);
  return "£" + num.toLocaleString("en-GB", { minimumFractionDigits: num % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 });
};

export const formatGBP2 = (value) => "£" + Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
